import Vue from 'vue';
import { LabelLayout, DEFAULT_LABEL_LAYOUT } from '@/types/shipping';

/**
 * Interface for printer configuration
 */
export interface PrinterConfiguration {
  labelPrinterId: string | null;
  pickTicketPrinterId: string | null;
  autoPrintLabels: boolean;
  autoPrintPickTickets: boolean;
  defaultLabelLayout: LabelLayout;
  lastUpdated?: string;
}

/**
 * Storage key constants for printer configuration
 */
export const PRINTER_CONFIG_KEYS = {
  // New unified keys
  LABEL_PRINTER_ID: 'printer_config.labelPrinterId',
  PICK_TICKET_PRINTER_ID: 'printer_config.pickTicketPrinterId',
  AUTO_PRINT_LABELS: 'printer_config.autoPrintLabels',
  AUTO_PRINT_PICK_TICKETS: 'printer_config.autoPrintPickTickets',
  DEFAULT_LABEL_LAYOUT: 'printer_config.defaultLabelLayout',
  LAST_UPDATED: 'printer_config.lastUpdated',

  // Legacy keys for migration
  LEGACY_SHIPPING_LABEL_PRINTER: 'shipping_label_printer_id',
  LEGACY_SHIPPING_AUTO_PRINT: 'shipping_label_auto_print',
  LEGACY_PICK_TICKET_PRINTER: 'pick_ticket_printer_id'
} as const;

/**
 * Singleton service for managing printer configuration across the application
 * Provides a single source of truth for printer settings and handles synchronization
 */
export class PrinterConfigurationService {
  private static instance: PrinterConfigurationService | null = null;
  private configuration: PrinterConfiguration;
  private eventBus: Vue;
  private initialized: boolean = false;

  private constructor() {
    this.eventBus = new Vue();
    this.configuration = this.getDefaultConfiguration();
    this.initialize();
  }

  /**
   * Get the singleton instance of PrinterConfigurationService
   */
  static getInstance(): PrinterConfigurationService {
    if (!PrinterConfigurationService.instance) {
      PrinterConfigurationService.instance = new PrinterConfigurationService();
    }
    return PrinterConfigurationService.instance;
  }

  /**
   * Initialize the service by loading and migrating settings
   */
  private initialize(): void {
    if (this.initialized) return;

    // First migrate old settings if they exist
    this.migrateOldSettings();

    // Then load the configuration
    this.loadFromLocalStorage();

    this.initialized = true;
    console.log('[PrinterConfigurationService] Initialized with configuration:', this.configuration);
  }

  /**
   * Get default configuration
   */
  private getDefaultConfiguration(): PrinterConfiguration {
    return {
      labelPrinterId: null,
      pickTicketPrinterId: null,
      autoPrintLabels: false,
      autoPrintPickTickets: false,
      defaultLabelLayout: DEFAULT_LABEL_LAYOUT,
      lastUpdated: new Date().toISOString()
    };
  }

  /**
   * Migrate old localStorage settings to new format
   */
  private migrateOldSettings(): void {
    const migrated: Partial<PrinterConfiguration> = {};
    let hasMigrations = false;

    // Migrate shipping label printer
    const legacyShippingPrinter = localStorage.getItem(PRINTER_CONFIG_KEYS.LEGACY_SHIPPING_LABEL_PRINTER);
    if (legacyShippingPrinter && !localStorage.getItem(PRINTER_CONFIG_KEYS.LABEL_PRINTER_ID)) {
      migrated.labelPrinterId = legacyShippingPrinter;
      localStorage.setItem(PRINTER_CONFIG_KEYS.LABEL_PRINTER_ID, legacyShippingPrinter);
      hasMigrations = true;
    }

    // Migrate auto print setting
    const legacyAutoPrint = localStorage.getItem(PRINTER_CONFIG_KEYS.LEGACY_SHIPPING_AUTO_PRINT);
    if (legacyAutoPrint && !localStorage.getItem(PRINTER_CONFIG_KEYS.AUTO_PRINT_LABELS)) {
      migrated.autoPrintLabels = legacyAutoPrint === 'true';
      localStorage.setItem(PRINTER_CONFIG_KEYS.AUTO_PRINT_LABELS, legacyAutoPrint);
      hasMigrations = true;
    }

    // Migrate pick ticket printer
    const legacyPickTicketPrinter = localStorage.getItem(PRINTER_CONFIG_KEYS.LEGACY_PICK_TICKET_PRINTER);
    if (legacyPickTicketPrinter && !localStorage.getItem(PRINTER_CONFIG_KEYS.PICK_TICKET_PRINTER_ID)) {
      // For now, use the same printer for both labels and pick tickets
      migrated.pickTicketPrinterId = legacyPickTicketPrinter;
      localStorage.setItem(PRINTER_CONFIG_KEYS.PICK_TICKET_PRINTER_ID, legacyPickTicketPrinter);

      // If no label printer was set, use the pick ticket printer
      if (!migrated.labelPrinterId && !localStorage.getItem(PRINTER_CONFIG_KEYS.LABEL_PRINTER_ID)) {
        migrated.labelPrinterId = legacyPickTicketPrinter;
        localStorage.setItem(PRINTER_CONFIG_KEYS.LABEL_PRINTER_ID, legacyPickTicketPrinter);
      }
      hasMigrations = true;
    }

    if (hasMigrations) {
      console.log('[PrinterConfigurationService] Migrated legacy settings:', migrated);

      // Optionally clean up old keys after successful migration
      // Commented out for safety - can be enabled after testing
      // localStorage.removeItem(PRINTER_CONFIG_KEYS.LEGACY_SHIPPING_LABEL_PRINTER);
      // localStorage.removeItem(PRINTER_CONFIG_KEYS.LEGACY_SHIPPING_AUTO_PRINT);
      // localStorage.removeItem(PRINTER_CONFIG_KEYS.LEGACY_PICK_TICKET_PRINTER);
    }
  }

  /**
   * Load configuration from localStorage
   */
  private loadFromLocalStorage(): void {
    const storedLayout = localStorage.getItem(PRINTER_CONFIG_KEYS.DEFAULT_LABEL_LAYOUT);
    const validLayouts: LabelLayout[] = ['4x6', '4x8', 'letter'];
    const defaultLabelLayout: LabelLayout = storedLayout && validLayouts.includes(storedLayout as LabelLayout)
      ? storedLayout as LabelLayout
      : DEFAULT_LABEL_LAYOUT;

    this.configuration = {
      labelPrinterId: localStorage.getItem(PRINTER_CONFIG_KEYS.LABEL_PRINTER_ID) || null,
      pickTicketPrinterId: localStorage.getItem(PRINTER_CONFIG_KEYS.PICK_TICKET_PRINTER_ID) || null,
      autoPrintLabels: localStorage.getItem(PRINTER_CONFIG_KEYS.AUTO_PRINT_LABELS) === 'true',
      autoPrintPickTickets: localStorage.getItem(PRINTER_CONFIG_KEYS.AUTO_PRINT_PICK_TICKETS) === 'true',
      defaultLabelLayout,
      lastUpdated: localStorage.getItem(PRINTER_CONFIG_KEYS.LAST_UPDATED) || new Date().toISOString()
    };
  }

  /**
   * Save configuration to localStorage
   */
  private saveToLocalStorage(): void {
    // Save each configuration item
    if (this.configuration.labelPrinterId !== null) {
      localStorage.setItem(PRINTER_CONFIG_KEYS.LABEL_PRINTER_ID, this.configuration.labelPrinterId);
    } else {
      localStorage.removeItem(PRINTER_CONFIG_KEYS.LABEL_PRINTER_ID);
    }

    if (this.configuration.pickTicketPrinterId !== null) {
      localStorage.setItem(PRINTER_CONFIG_KEYS.PICK_TICKET_PRINTER_ID, this.configuration.pickTicketPrinterId);
    } else {
      localStorage.removeItem(PRINTER_CONFIG_KEYS.PICK_TICKET_PRINTER_ID);
    }

    localStorage.setItem(PRINTER_CONFIG_KEYS.AUTO_PRINT_LABELS, String(this.configuration.autoPrintLabels));
    localStorage.setItem(PRINTER_CONFIG_KEYS.AUTO_PRINT_PICK_TICKETS, String(this.configuration.autoPrintPickTickets));
    localStorage.setItem(PRINTER_CONFIG_KEYS.DEFAULT_LABEL_LAYOUT, this.configuration.defaultLabelLayout);
    localStorage.setItem(PRINTER_CONFIG_KEYS.LAST_UPDATED, new Date().toISOString());

    // Also update legacy keys for backward compatibility during transition
    if (this.configuration.labelPrinterId !== null) {
      localStorage.setItem(PRINTER_CONFIG_KEYS.LEGACY_SHIPPING_LABEL_PRINTER, this.configuration.labelPrinterId);
    }
    localStorage.setItem(PRINTER_CONFIG_KEYS.LEGACY_SHIPPING_AUTO_PRINT, String(this.configuration.autoPrintLabels));
    if (this.configuration.pickTicketPrinterId !== null) {
      localStorage.setItem(PRINTER_CONFIG_KEYS.LEGACY_PICK_TICKET_PRINTER, this.configuration.pickTicketPrinterId);
    }
  }

  /**
   * Emit configuration change event
   */
  private emitConfigurationChange(): void {
    this.eventBus.$emit('configuration-changed', this.configuration);

    // Also emit on the global Vue bus for components that might be listening
    const globalBus = (window as any).Vue?.prototype?.$bus;
    if (globalBus) {
      globalBus.$emit('printer-settings-updated', {
        autoPrintEnabled: this.configuration.autoPrintLabels,
        selectedPrinterId: this.configuration.labelPrinterId,
        pickTicketPrinterId: this.configuration.pickTicketPrinterId,
        configuration: this.configuration
      });
    }
  }

  // === Public API Methods ===

  /**
   * Get the complete configuration
   */
  getConfiguration(): PrinterConfiguration {
    return { ...this.configuration };
  }

  /**
   * Set the label printer
   */
  setLabelPrinter(printerId: string | null): void {
    if (this.configuration.labelPrinterId === printerId) return;

    this.configuration.labelPrinterId = printerId;
    this.configuration.lastUpdated = new Date().toISOString();

    // If pick ticket printer is not set, use the same printer
    if (!this.configuration.pickTicketPrinterId && printerId) {
      this.configuration.pickTicketPrinterId = printerId;
    }

    this.saveToLocalStorage();
    this.emitConfigurationChange();

    console.log('[PrinterConfigurationService] Label printer updated:', printerId);
  }

  /**
   * Get the label printer ID
   */
  getLabelPrinter(): string | null {
    return this.configuration.labelPrinterId;
  }

  /**
   * Set the pick ticket printer
   */
  setPickTicketPrinter(printerId: string | null): void {
    if (this.configuration.pickTicketPrinterId === printerId) return;

    this.configuration.pickTicketPrinterId = printerId;
    this.configuration.lastUpdated = new Date().toISOString();

    this.saveToLocalStorage();
    this.emitConfigurationChange();

    console.log('[PrinterConfigurationService] Pick ticket printer updated:', printerId);
  }

  /**
   * Get the pick ticket printer ID
   */
  getPickTicketPrinter(): string | null {
    // If no specific pick ticket printer is set, fall back to label printer
    return this.configuration.pickTicketPrinterId || this.configuration.labelPrinterId;
  }

  /**
   * Set auto-print for labels
   */
  setAutoPrintLabels(enabled: boolean): void {
    if (this.configuration.autoPrintLabels === enabled) return;

    this.configuration.autoPrintLabels = enabled;
    this.configuration.lastUpdated = new Date().toISOString();

    this.saveToLocalStorage();
    this.emitConfigurationChange();

    console.log('[PrinterConfigurationService] Auto-print labels updated:', enabled);
  }

  /**
   * Get auto-print labels setting
   */
  getAutoPrintLabels(): boolean {
    return this.configuration.autoPrintLabels;
  }

  /**
   * Set auto-print for pick tickets
   */
  setAutoPrintPickTickets(enabled: boolean): void {
    if (this.configuration.autoPrintPickTickets === enabled) return;

    this.configuration.autoPrintPickTickets = enabled;
    this.configuration.lastUpdated = new Date().toISOString();

    this.saveToLocalStorage();
    this.emitConfigurationChange();

    console.log('[PrinterConfigurationService] Auto-print pick tickets updated:', enabled);
  }

  /**
   * Get auto-print pick tickets setting
   */
  getAutoPrintPickTickets(): boolean {
    return this.configuration.autoPrintPickTickets;
  }

  /**
   * Set the default label layout
   */
  setDefaultLabelLayout(layout: LabelLayout): void {
    if (this.configuration.defaultLabelLayout === layout) return;

    this.configuration.defaultLabelLayout = layout;
    this.configuration.lastUpdated = new Date().toISOString();

    this.saveToLocalStorage();
    this.emitConfigurationChange();

    console.log('[PrinterConfigurationService] Default label layout updated:', layout);
  }

  /**
   * Get the default label layout
   */
  getDefaultLabelLayout(): LabelLayout {
    return this.configuration.defaultLabelLayout;
  }

  /**
   * Update the entire configuration at once
   */
  updateConfiguration(config: Partial<PrinterConfiguration>): void {
    const hasChanges = Object.keys(config).some(key => {
      return (this.configuration as any)[key] !== (config as any)[key];
    });

    if (!hasChanges) return;

    this.configuration = {
      ...this.configuration,
      ...config,
      lastUpdated: new Date().toISOString()
    };

    this.saveToLocalStorage();
    this.emitConfigurationChange();

    console.log('[PrinterConfigurationService] Configuration updated:', this.configuration);
  }

  /**
   * Register a callback for configuration changes
   */
  onConfigurationChange(callback: (config: PrinterConfiguration) => void): () => void {
    this.eventBus.$on('configuration-changed', callback);

    // Return unsubscribe function
    return () => {
      this.eventBus.$off('configuration-changed', callback);
    };
  }

  /**
   * Clear all printer configuration
   */
  clearConfiguration(): void {
    this.configuration = this.getDefaultConfiguration();

    // Clear all storage keys
    Object.values(PRINTER_CONFIG_KEYS).forEach(key => {
      localStorage.removeItem(key);
    });

    this.emitConfigurationChange();

    console.log('[PrinterConfigurationService] Configuration cleared');
  }

  /**
   * Check if a printer is configured
   */
  hasConfiguredPrinter(): boolean {
    return !!(this.configuration.labelPrinterId || this.configuration.pickTicketPrinterId);
  }

  /**
   * Get printer for specific type
   */
  getPrinterForType(type: 'label' | 'pickTicket'): string | null {
    if (type === 'label') {
      return this.getLabelPrinter();
    } else {
      return this.getPickTicketPrinter();
    }
  }
}

// Export singleton instance getter for convenience
export const getPrinterConfiguration = (): PrinterConfigurationService => {
  return PrinterConfigurationService.getInstance();
};